import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

interface ShareItem {
  id: string;
  type: 'song' | 'album' | 'video' | 'playlist' | 'artist';
  title: string;
  url: string;
  image?: string;
}

interface ShareState {
  isOpen: boolean;
  item: ShareItem | null;
}

interface ShareContextType {
  state: ShareState;
  openShare: (item: ShareItem) => void;
  closeShare: () => void;
  shareToTwitter: (item?: ShareItem) => void;
  shareToFacebook: (item?: ShareItem) => void;
  copyLink: (item?: ShareItem) => Promise<void>;
  shareNative: (item?: ShareItem) => Promise<void>;
}

const ShareContext = createContext<ShareContextType | null>(null);

export const ShareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ShareState>({ isOpen: false, item: null });
  const { user } = useAuth();

  const recordShare = useCallback(async (item: ShareItem, platform: string) => {
    if (!user) return;
    try {
      await supabase.from('shares').insert({
        user_id: user.id,
        content_type: item.type,
        content_id: item.id,
        platform,
      });
    } catch {}
  }, [user]);

  const openShare = useCallback((item: ShareItem) => {
    setState({ isOpen: true, item });
  }, []);

  const closeShare = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const shareToTwitter = useCallback((item?: ShareItem) => {
    const target = item || state.item;
    if (!target) return;
    const text = encodeURIComponent(`Check out "${target.title}" on Vision Music!`);
    const url = encodeURIComponent(target.url);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    recordShare(target, 'twitter');
  }, [state.item, recordShare]);

  const shareToFacebook = useCallback((item?: ShareItem) => {
    const target = item || state.item;
    if (!target) return;
    const url = encodeURIComponent(target.url);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    recordShare(target, 'facebook');
  }, [state.item, recordShare]);

  const copyLink = useCallback(async (item?: ShareItem) => {
    const target = item || state.item;
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target.url);
      toast.success('Link copied to clipboard');
      recordShare(target, 'copy');
    } catch {
      toast.error('Failed to copy link');
    }
  }, [state.item, recordShare]);

  const shareNative = useCallback(async (item?: ShareItem) => {
    const target = item || state.item;
    if (!target) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: target.title, url: target.url });
        recordShare(target, 'native');
      } catch {}
    } else {
      await copyLink(target);
    }
  }, [state.item, copyLink, recordShare]);

  return (
    <ShareContext.Provider value={{ state, openShare, closeShare, shareToTwitter, shareToFacebook, copyLink, shareNative }}>
      {children}
    </ShareContext.Provider>
  );
};

export const useShare = () => {
  const context = useContext(ShareContext);
  if (!context) throw new Error('useShare must be used within ShareProvider');
  return context;
};
